import { i18n } from '@lingui/core';
import { Button, Center, Checkbox, Group, Loader, Select, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { openModal, closeAllModals } from '@mantine/modals';
import { FC, useState } from 'react';
import { PositionGroupDTO } from '../../../Service/DocumentT';

interface SelectGroupModalProps {
  groups: PositionGroupDTO[];
  currentGroupId: string | null;
  onFinish: (groupId: string | null) => Promise<boolean>;
}

export const openSelectGroupModal = (props: SelectGroupModalProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Gruppe ändern
      </Text>
    ),
    // eslint-disable-next-line react/jsx-props-no-spreading
    children: <SelectGroupModal {...props} />,
  });
};

const SelectGroupModal: FC<SelectGroupModalProps> = ({ groups, currentGroupId, onFinish }) => {
  const noGroupValue = '__NO_POSITION_GROUP__';
  const initialValue = currentGroupId ?? noGroupValue;
  const [groupId, selectGroup] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const data = [
    { label: 'Keine Gruppe', value: noGroupValue },
    ...groups.map((group) => ({ label: group.name, value: group.id })),
  ];

  const onClick = async () => {
    if (groupId) {
      setLoading(true);
      const ok = await onFinish(groupId === noGroupValue ? null : groupId);
      setLoading(false);
      if (ok) {
        closeAllModals();
      }
    }
  };

  return (
    <>
      <Select label="Positionsgruppe" value={groupId} data={data} onChange={(val) => val && selectGroup(val)} />
      <Center>
        <Button
          mt="xl"
          disabled={!groupId || groupId === initialValue}
          onClick={onClick}
          sx={{ width: 200 }}
          leftIcon={loading && <Loader size="xs" />}
        >
          {i18n._('actions.apply')}
        </Button>
      </Center>
    </>
  );
};

interface DeletePositionGroupProps {
  label: string;
  onConfirm: (removePositions: boolean) => Promise<boolean>;
  positionsPresent: boolean;
}

export const openDeletePositionGroupModal = (props: DeletePositionGroupProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Gruppe löschen
      </Text>
    ),
    // eslint-disable-next-line react/jsx-props-no-spreading
    children: <DeletePositionGroupModal {...props} />,
  });
};

const DeletePositionGroupModal: FC<DeletePositionGroupProps> = ({ label, onConfirm, positionsPresent }) => {
  const [loading, setLoading] = useState(false);
  const [removePositions, { toggle }] = useDisclosure(false);

  const onClick = async () => {
    setLoading(true);
    const deleted = await onConfirm(removePositions);
    setLoading(false);
    if (deleted) {
      closeAllModals();
    }
  };

  return (
    <>
      <Text>
        Möchten Sie Gruppe{' '}
        <Text span weight="bolder">
          {label}
        </Text>{' '}
        wirklich entfernen?
      </Text>
      {positionsPresent && (
        <Checkbox mt="md" checked={removePositions} onChange={toggle} label="Gruppenpositionen entfernen" />
      )}
      <Group position="right" spacing="xs" mt="xl">
        <Button color="gray" variant="subtle" onClick={() => closeAllModals()}>
          {i18n._('actions.cancel')}
        </Button>
        <Button
          disabled={loading}
          onClick={onClick}
          leftIcon={loading && <Loader size="xs" />}
          color="red"
          variant="subtle"
          sx={{ backgroundColor: '#FFEFEF' }}
        >
          {i18n._('actions.delete')}
        </Button>
      </Group>
    </>
  );
};
